import AdminTable from '../../../components/admin/AdminTable';
import React, { useState, useEffect, useCallback } from 'react';
import { Edit, Trash2, History, Plus, Notebook } from 'lucide-react';
import {
    fetchCodigoSesionPosConfigs,
    CodigoSesionPosConfig
} from '../../../services/codigoSesionPosService';
import AddCodigoSesionPos from '../../../components/OrdenSesionPos/AddCodigoSsesionPos';
import EditCodigoSesionPos from '../../../components/OrdenSesionPos/EditCodigoSesionPos';
import DeleteCodigoSesionPos from '../../../components/OrdenSesionPos/DeleteCodigoSesionPos';
import Pagination from '../../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { useModelPermission } from '../../../hooks/useModelPermission';
import Loading from '../../../components/Loading';

const CodigoSesionPosPage: React.FC = () => {
    const [configs, setConfigs] = useState<CodigoSesionPosConfig[]>([]);
    const [showAddModal, setShowAddModal] = useState<boolean>(false);
    const [showEditModal, setShowEditModal] = useState<boolean>(false);
    const [selectedConfig, setSelectedConfig] = useState<CodigoSesionPosConfig | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [deleteConfig, setDeleteConfig] = useState<CodigoSesionPosConfig | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const itemsPerPage = 10;

    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout, user } = useAuth();

    const userLevelId = user?.user_level_id ?? 0;
    // Ajusta el nombre del modelo según tu backend (p.ej. 'codigosesionposconfig' o 'codigo_sesion_pos_config')
    const perm = useModelPermission('codigosesionposconfig', userLevelId);

    const loadConfigs = useCallback(async () => {
        setLoading(true);
        try {
            if (perm?.can_read) {
                const data = await fetchCodigoSesionPosConfigs();
                const sortedData = [...data].sort((a, b) => a.id - b.id);
                setConfigs(sortedData);
            } else {
                setConfigs([]);
            }
        } catch (error) {
            /* Browser diagnostics omitted. */
        } finally {
            setLoading(false);
        }
    }, [perm]);

    useEffect(() => {
        if (perm === null) return; // esperar a que los permisos se carguen
        loadConfigs();
    }, [perm, loadConfigs]);

    const totalPages = Math.ceil(configs.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const displayedConfigs = configs.slice(startIndex, startIndex + itemsPerPage);

    const handleCreate = () => setShowAddModal(true);

    const handleConfigCreated = (newConfig: CodigoSesionPosConfig) => {
        setConfigs(prev => {
            const updatedConfigs = prev.map(cfg =>
                newConfig.activo ? { ...cfg, activo: false } : cfg
            );
            return [...updatedConfigs, newConfig].sort((a, b) => a.id - b.id);
        });
        setShowAddModal(false);
    };

    const handleConfigUpdated = (updatedConfig: CodigoSesionPosConfig) => {
        setConfigs(prev => {
            return prev.map(cfg => {
                if (cfg.id === updatedConfig.id) {
                    return updatedConfig;
                }
                return updatedConfig.activo ? { ...cfg, activo: false } : cfg;
            });
        });
        setShowEditModal(false);
        setSelectedConfig(null);
    };

    const handleEdit = (cfg: CodigoSesionPosConfig) => {
        setSelectedConfig(cfg);
        setShowEditModal(true);
    };

    const handleConfigDeleted = (deletedId: number) => {
        setConfigs(prev => prev.filter(cfg => cfg.id !== deletedId));
        setShowDeleteModal(false);
        setDeleteConfig(null);
    };

    const handleDeleteClick = (cfg: CodigoSesionPosConfig) => {
        setDeleteConfig(cfg);
        setShowDeleteModal(true);
    };

    if (perm === null || (loading && configs.length === 0)) {
        return <Loading text="Cargando sesiones pos..." />;
    }

    return (
        <div data-admin-page="true" className="space-y-6 mx-auto px-4 sm:px-6 lg:px-8 max-w-9xl">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-xl font-bold text-gray-800 admin-page-title">Gestión de <span className="admin-title-accent">Código de Sesión POS</span></h2>
                    <div className="mt-1 flex items-center space-x-2 text-sm text-gray-500">
                        <a href="/dashboard" className="hover:text-[#c1348b]">Dashboard</a>
                        <span>/</span>
                        <a href="/codigos-sesion-pos" className="text-[#c1348b] hover:text-[#8f1f64]">Código de Sesión POS</a>
                    </div>
                </div>

                <div className="flex gap-2 items-center">
                    {perm.can_read && (
                        <button
                            onClick={loadConfigs}
                            className="bg-[#61257b] text-white px-4 py-2 rounded-lg hover:bg-[#4a1c5e] flex items-center h-[39px] text-sm font-medium transition-colors"
                            title="Refrescar lista"
                        >
                            <History className="w-5 h-5" />
                        </button>
                    )}

                    {perm.can_create && (
                        <button
                            onClick={handleCreate}
                            className="bg-[#c1348b] text-white px-4 py-2 rounded-lg hover:bg-[#8f1f64] flex items-center h-[39px] text-sm font-medium transition-colors min-w-[170px]"
                        >
                            <Plus className="w-5 h-5 mr-2" />
                            Agregar Código de Sesión POS
                        </button>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
                <ExpiredSessionModal
                    isOpen={isSessionExpired}
                    onClose={async () => {
                        setIsSessionExpired(false);
                        await logout();
                    }}
                />

                {showAddModal && (
                    <AddCodigoSesionPos
                        onClose={() => setShowAddModal(false)}
                        onCreated={handleConfigCreated}
                    />
                )}
                {showEditModal && selectedConfig && (
                    <EditCodigoSesionPos
                        config={selectedConfig}
                        onClose={() => {
                            setShowEditModal(false);
                            setSelectedConfig(null);
                        }}
                        onUpdated={handleConfigUpdated}
                    />
                )}
                {showDeleteModal && deleteConfig && (
                    <DeleteCodigoSesionPos
                        id={deleteConfig.id}
                        prefijo={deleteConfig?.prefijo ?? ''}
                        onClose={() => {
                            setShowDeleteModal(false);
                            setDeleteConfig(null);
                        }}
                        onDeleted={handleConfigDeleted}
                    />
                )}

                <div className="overflow-x-auto">
                    <AdminTable className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prefijo</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha Creación</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {!perm.can_read ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-12 text-center">
                                        <div className="flex flex-col items-center justify-center space-y-3">
                                            <Notebook className="w-12 h-12 text-gray-400" />
                                            <span className="text-lg font-medium text-gray-600">
                                                No tienes permiso para ver las configuraciones
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            ) : loading ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-12 text-center">
                                        <div className="flex flex-col items-center justify-center space-y-3">
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                                            <span className="text-lg font-medium text-gray-600">
                                                Cargando configuraciones...
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            ) : displayedConfigs.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-12 text-center">
                                        <div className="flex flex-col items-center justify-center space-y-3">
                                            <Notebook className="w-12 h-12 text-gray-400" />
                                            <span className="text-lg font-medium text-gray-600">
                                                No hay configuraciones disponibles
                                            </span>
                                            {perm.can_create && (
                                                <p className="text-gray-500 text-sm">
                                                    Agrega una nueva configuración para comenzar
                                                </p>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                displayedConfigs.map(cfg => (
                                    <tr key={cfg.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-4 text-sm text-gray-900">{cfg.nombre}</td>
                                        <td className="px-4 py-4 text-sm text-gray-500 font-mono">{cfg.prefijo}</td>
                                        <td className="px-4 py-4 text-sm text-gray-500">
                                            {new Date(cfg.fecha_creacion).toLocaleDateString('es-ES', {
                                                day: '2-digit',
                                                month: '2-digit',
                                                year: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}
                                        </td>
                                        <td className="px-4 py-4">
                                            {cfg.activo ? (
                                                <span className="px-2 inline-flex text-xs font-medium rounded-full bg-green-100 text-green-800">Activo</span>
                                            ) : (
                                                <span className="px-2 inline-flex text-xs font-medium rounded-full bg-red-100 text-red-800">Inactivo</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-4">
                                            <div className="flex gap-2">
                                                {perm.can_update && (
                                                    <button
                                                        onClick={() => handleEdit(cfg)}
                                                        className="p-1.5 rounded-full bg-blue-50 hover:bg-blue-100 text-[#c1348b] hover:text-[#8f1f64] transition"
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                    </button>
                                                )}
                                                {perm.can_delete && (
                                                    <button
                                                        onClick={() => handleDeleteClick(cfg)}
                                                        className="p-1.5 rounded-full bg-red-50 hover:bg-red-100 text-red-600 hover:text-red-800 transition"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </AdminTable>
                </div>

                {/* Paginación: solo si hay datos y permiso de lectura */}
                {perm.can_read && !loading && configs.length > 0 && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        itemsPerPage={itemsPerPage}
                        totalItems={configs.length}
                    />
                )}
            </div>
        </div>
    );
};

export default CodigoSesionPosPage;
